import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2, Calendar, Clock, MapPin, Users, ListChecks, FileText, ArrowUpRight, Save, X, Edit3, Check } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { v: 'scheduled', label: 'Scheduled' },
  { v: 'completed', label: 'Completed' },
  { v: 'cancelled', label: 'Cancelled' },
];

export default function MeetingsPanel({ api }) {
  const [meetings, setMeetings] = useState([]);
  const [scope, setScope] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ceo-life/meetings', { params: { scope } });
      setMeetings(data || []);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [api, scope]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const open = (m) => { setActive(m); setEditing(false); setCreating(false); setDraft({ ...m }); };
  const startCreate = () => { setActive(null); setCreating(true); setEditing(true); setDraft(empty()); };

  const save = async () => {
    if (!draft.title.trim()) { toast.error('Title required'); return; }
    try {
      if (draft.id) {
        const { data } = await api.put(`/ceo-life/meetings/${draft.id}`, draft);
        setMeetings(prev => prev.map(m => m.id === data.id ? data : m));
        setActive(data);
        toast.success('Updated');
      } else {
        const { data } = await api.post('/ceo-life/meetings', draft);
        setMeetings(prev => [data, ...prev]);
        setActive(data);
        setCreating(false);
        toast.success('Meeting added');
      }
      setEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await api.delete(`/ceo-life/meetings/${id}`);
      setMeetings(prev => prev.filter(m => m.id !== id));
      if (active?.id === id) { setActive(null); setEditing(false); }
    } catch { toast.error('Failed'); }
  };

  const toggleAction = async (idx) => {
    const next = (active.action_items || []).map((a, i) => i === idx ? { ...a, done: !a.done } : a);
    try {
      const { data } = await api.put(`/ceo-life/meetings/${active.id}`, { action_items: next });
      setActive(data); setDraft(data);
      setMeetings(prev => prev.map(m => m.id === data.id ? data : m));
    } catch { toast.error('Failed'); }
  };

  const promoteActions = async () => {
    if (!active?.id) return;
    try {
      const { data } = await api.post(`/ceo-life/meetings/${active.id}/promote-actions`);
      toast.success(`${data.spawned} action item${data.spawned===1?'':'s'} → To-Dos`);
    } catch { toast.error('Failed'); }
  };

  const pushToGoogle = async () => {
    if (!active?.id) return;
    try {
      await api.post('/calendar/push/meeting', { meeting_id: active.id });
      toast.success('Pushed to Google Calendar');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Google push failed — connect Google Calendar first'); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="ceo-meetings" style={{ minHeight: 500 }}>
      <div className="md:col-span-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {['upcoming','past','all'].map(s => (
              <button key={s} onClick={() => setScope(s)} className={`ceo-tab-btn ${scope===s?'active':''}`} style={{ fontSize: 11, padding: '5px 10px', textTransform: 'capitalize' }} data-testid={`ceo-meet-scope-${s}`}>{s}</button>
            ))}
          </div>
          <button onClick={startCreate} className="ceo-btn ceo-btn-primary ml-auto" data-testid="ceo-meet-new"><Plus className="h-3.5 w-3.5" /></button>
        </div>

        <div className="ceo-card-elevated overflow-hidden" style={{ maxHeight: 540, overflowY: 'auto' }}>
          {meetings.length === 0 ? (
            <div className="ceo-empty m-2 border-0 p-6"><Calendar className="ceo-empty-icon h-8 w-8" /><p className="text-xs">No {scope} meetings yet.</p></div>
          ) : meetings.map(m => (
            <button key={m.id} onClick={() => open(m)} className="w-full text-left px-4 py-3 block transition-colors"
              style={{ background: active?.id===m.id?'var(--ink-3)':'transparent', borderBottom:'1px solid var(--line)', borderLeft: active?.id===m.id?'2px solid var(--accent)':'2px solid transparent' }}
              data-testid={`ceo-meeting-${m.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{m.title}</span>
                {m.status === 'cancelled' && <span className="ceo-pill" style={{ padding:'1px 6px', fontSize:9, color:'var(--danger)' }}>cancelled</span>}
              </div>
              <div className="text-[11px] mt-0.5 flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-low)' }}>
                <span className="flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />{formatDate(m.date)}{m.start_time?` · ${m.start_time}`:''}</span>
                {(m.attendees||[]).length > 0 && <span className="flex items-center gap-1"><Users className="h-2.5 w-2.5" />{m.attendees.length}</span>}
                {(m.action_items||[]).length > 0 && <span className="flex items-center gap-1"><ListChecks className="h-2.5 w-2.5" />{(m.action_items||[]).filter(a=>!a.done).length} open</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="md:col-span-7">
        {(active || creating) ? (
          editing ? (
            <MeetingForm draft={draft} setDraft={setDraft} onCancel={() => { setEditing(false); setCreating(false); if (active) setDraft({ ...active }); }} onSave={save} />
          ) : (
            <MeetingView m={active} onEdit={() => setEditing(true)} onDelete={() => remove(active.id)} onToggleAction={toggleAction} onPromote={promoteActions} onPushGoogle={pushToGoogle} />
          )
        ) : (
          <div className="ceo-empty h-full flex flex-col items-center justify-center"><Calendar className="ceo-empty-icon h-10 w-10" /><p className="text-sm" style={{ color: 'var(--text-dim)' }}>Pick a meeting or hit <strong style={{ color: 'var(--text)' }}>+</strong> to schedule one.</p></div>
        )}
      </div>
    </div>
  );
}

function MeetingView({ m, onEdit, onDelete, onToggleAction, onPromote, onPushGoogle }) {
  const openActions = (m.action_items||[]).filter(a => !a.done).length;
  const synced = !!m.google_event_id;
  return (
    <div className="ceo-card-elevated p-5 space-y-4" data-testid="ceo-meeting-view">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="ceo-headline text-2xl">{m.title}</h2>
          <div className="flex items-center gap-3 mt-1 flex-wrap text-sm" style={{ color: 'var(--text-dim)' }}>
            <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{formatDate(m.date)}</span>
            {(m.start_time || m.end_time) && <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{m.start_time}{m.end_time?` – ${m.end_time}`:''}</span>}
            {m.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{m.location}</span>}
            {synced && (
              <a href={m.google_html_link} target="_blank" rel="noreferrer" className="flex items-center gap-1.5" style={{ color: '#4CD964' }} data-testid="ceo-meet-google-link">
                <Check className="h-3 w-3" /> Synced to Google
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onPushGoogle} className="ceo-btn ceo-btn-ghost" data-testid="ceo-meet-push-google" title={synced ? 'Update on Google Calendar' : 'Push to Google Calendar'}>
            <Calendar className="h-3 w-3" />{synced ? 'Sync' : 'To Google'}
          </button>
          <button onClick={onEdit} className="ceo-btn ceo-btn-ghost" data-testid="ceo-meet-edit"><Edit3 className="h-3 w-3" />Edit</button>
          <button onClick={onDelete} className="ceo-btn ceo-btn-danger" data-testid="ceo-meet-delete"><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>

      {(m.attendees||[]).length > 0 && (
        <div>
          <div className="ceo-eyebrow flex items-center gap-1.5 mb-2"><Users className="h-2.5 w-2.5" />Attendees</div>
          <div className="flex flex-wrap gap-1.5">
            {m.attendees.map(a => <span key={a} className="ceo-pill" style={{ fontSize: 11 }}>{a}</span>)}
          </div>
        </div>
      )}

      {(m.agenda||[]).length > 0 && (
        <div>
          <div className="ceo-eyebrow mb-2">Agenda</div>
          <ol className="space-y-1.5">
            {m.agenda.map((a, i) => (
              <li key={a.id||i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text)' }}>
                <span style={{ color: 'var(--text-low)', minWidth: 18 }}>{i+1}.</span>
                <span className="flex-1">{a.title}</span>
                {a.duration_min > 0 && <span className="ceo-pill" style={{ padding:'1px 6px', fontSize:10 }}>{a.duration_min}m</span>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {m.prep_notes && (
        <div>
          <div className="ceo-eyebrow flex items-center gap-1.5 mb-2"><FileText className="h-2.5 w-2.5" />Prep notes</div>
          <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)', lineHeight: 1.6 }}>{m.prep_notes}</p>
        </div>
      )}

      {(m.action_items||[]).length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="ceo-eyebrow flex items-center gap-1.5"><ListChecks className="h-2.5 w-2.5" />Action items {openActions>0 && <span className="ceo-pill" style={{ padding:'1px 6px', fontSize:9 }}>{openActions} open</span>}</div>
            {openActions > 0 && <button onClick={onPromote} className="ceo-btn ceo-btn-ghost" style={{ padding:'4px 8px', fontSize:11 }} data-testid="ceo-meet-promote"><ArrowUpRight className="h-3 w-3" />Send to To-Dos</button>}
          </div>
          <div className="space-y-1">
            {m.action_items.map((a, i) => (
              <div key={a.id||i} className={`ceo-todo-row ${a.done?'done':''}`} data-testid={`ceo-action-${i}`}>
                <input type="checkbox" className="ceo-checkbox" checked={!!a.done} onChange={() => onToggleAction(i)} style={{ marginTop: 2 }} />
                <div className="flex-1 min-w-0">
                  <div className="ceo-todo-title">{a.title}</div>
                  {(a.assigned_to || a.due_date) && (
                    <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-low)' }}>
                      {a.assigned_to && <>→ {a.assigned_to}</>}{a.assigned_to && a.due_date && <> · </>}{a.due_date}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {m.follow_up && (
        <div>
          <div className="ceo-eyebrow mb-2">Follow-up summary</div>
          <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)', lineHeight: 1.6 }}>{m.follow_up}</p>
        </div>
      )}
    </div>
  );
}

function MeetingForm({ draft, setDraft, onCancel, onSave }) {
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  const setAgendaItem = (i, k, v) => set('agenda', (draft.agenda||[]).map((a, idx) => idx===i?{...a,[k]:v}:a));
  const addAgenda = () => set('agenda', [...(draft.agenda||[]), { id:`a-${Date.now()}`, title:'', duration_min:0 }]);
  const removeAgenda = (i) => set('agenda', (draft.agenda||[]).filter((_,idx)=>idx!==i));
  const setActionItem = (i, k, v) => set('action_items', (draft.action_items||[]).map((a, idx) => idx===i?{...a,[k]:v}:a));
  const addAction = () => set('action_items', [...(draft.action_items||[]), { id:`ai-${Date.now()}`, title:'', assigned_to:'', priority:'normal' }]);
  const removeAction = (i) => set('action_items', (draft.action_items||[]).filter((_,idx)=>idx!==i));

  return (
    <div className="ceo-card-elevated p-5 space-y-3" data-testid="ceo-meet-form">
      <h3 className="ceo-headline text-xl">{draft.id ? 'Edit meeting' : 'New meeting'}</h3>
      <input className="ceo-input" placeholder="Meeting title" autoFocus value={draft.title||''} onChange={e=>set('title',e.target.value)} data-testid="ceo-meet-title" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div><label className="ceo-eyebrow">Date</label><input type="date" className="ceo-input" value={draft.date||''} onChange={e=>set('date',e.target.value)} /></div>
        <div><label className="ceo-eyebrow">Start</label><input type="time" className="ceo-input" value={draft.start_time||''} onChange={e=>set('start_time',e.target.value)} /></div>
        <div><label className="ceo-eyebrow">End</label><input type="time" className="ceo-input" value={draft.end_time||''} onChange={e=>set('end_time',e.target.value)} /></div>
        <div><label className="ceo-eyebrow">Status</label>
          <select className="ceo-select" value={draft.status||'scheduled'} onChange={e=>set('status',e.target.value)}>{STATUS_OPTIONS.map(s=><option key={s.v} value={s.v}>{s.label}</option>)}</select>
        </div>
      </div>
      <input className="ceo-input" placeholder="Location or video link" value={draft.location||''} onChange={e=>set('location',e.target.value)} />
      <div>
        <label className="ceo-eyebrow">Attendees (comma separated)</label>
        <input className="ceo-input" placeholder="Tom, Sara, Mike..." value={(draft.attendees||[]).join(', ')} onChange={e=>set('attendees', e.target.value.split(',').map(s=>s.trim()).filter(Boolean))} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="ceo-eyebrow">Agenda</label>
          <button onClick={addAgenda} className="ceo-btn ceo-btn-ghost" style={{ padding:'3px 8px', fontSize:10 }}><Plus className="h-2.5 w-2.5" />Add item</button>
        </div>
        {(draft.agenda||[]).map((a, i) => (
          <div key={a.id||i} className="flex gap-2 mb-1.5">
            <input className="ceo-input flex-1" placeholder="Discussion topic" value={a.title||''} onChange={e=>setAgendaItem(i,'title',e.target.value)} />
            <input type="number" className="ceo-input" style={{ width: 80 }} placeholder="min" value={a.duration_min||''} onChange={e=>setAgendaItem(i,'duration_min',e.target.value)} />
            <button onClick={()=>removeAgenda(i)} className="ceo-btn ceo-btn-danger" style={{ padding: '0 8px' }}><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>

      <div>
        <label className="ceo-eyebrow">Prep notes</label>
        <textarea rows={3} className="ceo-textarea" placeholder="Background, key questions, what success looks like..." value={draft.prep_notes||''} onChange={e=>set('prep_notes',e.target.value)} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="ceo-eyebrow">Action items</label>
          <button onClick={addAction} className="ceo-btn ceo-btn-ghost" style={{ padding:'3px 8px', fontSize:10 }}><Plus className="h-2.5 w-2.5" />Add action</button>
        </div>
        {(draft.action_items||[]).map((a, i) => (
          <div key={a.id||i} className="grid grid-cols-12 gap-2 mb-1.5">
            <input className="ceo-input col-span-6" placeholder="What needs doing" value={a.title||''} onChange={e=>setActionItem(i,'title',e.target.value)} />
            <input className="ceo-input col-span-3" placeholder="Assigned to" value={a.assigned_to||''} onChange={e=>setActionItem(i,'assigned_to',e.target.value)} />
            <input type="date" className="ceo-input col-span-2" value={a.due_date||''} onChange={e=>setActionItem(i,'due_date',e.target.value)} />
            <button onClick={()=>removeAction(i)} className="ceo-btn ceo-btn-danger col-span-1" style={{ padding: '0 8px' }}><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>

      <div>
        <label className="ceo-eyebrow">Follow-up summary</label>
        <textarea rows={2} className="ceo-textarea" placeholder="Decisions, key takeaways..." value={draft.follow_up||''} onChange={e=>set('follow_up',e.target.value)} />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="ceo-btn ceo-btn-ghost"><X className="h-3 w-3" />Cancel</button>
        <button onClick={onSave} className="ceo-btn ceo-btn-primary" data-testid="ceo-meet-save"><Save className="h-3 w-3" />{draft.id?'Update':'Add meeting'}</button>
      </div>
    </div>
  );
}

function empty() {
  return { title:'', date:'', start_time:'', end_time:'', location:'', attendees:[], agenda:[], prep_notes:'', action_items:[], follow_up:'', status:'scheduled' };
}

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' }); }
  catch { return d; }
}

